import { Flex, Group, Space, Title, UnstyledButton, Text,TabsValue } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { shallowEqual } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deleteCurrent, getInfosIndustry, setIsAuthorizeAction, updateCompanyName, updateCurrent, updateCurrentUserOrganizationInfo, updateInfosIndustry } from 'src/store';

import classNames from 'classnames';
import styles from './styles.module.scss';
import { Button, Divider, Modal, Select, TextInput } from '@common/components';
import { Auth } from 'aws-amplify';
import * as yup from 'yup';
import { validationPattern } from '@common/helpers';
import { useForm, yupResolver } from '@mantine/form';
import { useDisclosure, useDocumentTitle } from '@mantine/hooks';
import { useAlert, useFormFocus } from '@common/hooks';
import { removetOnboardingStatus } from '../../helpers/localStorageHelper';
import { PATHS } from '../../constants';
import MyOrganisations from '../MyOrganisations';

const AccountPage = (): JSX.Element => {
  const accountRef = useRef<HTMLDivElement>(null);
  const passwordRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const organizationRef = useRef<HTMLDivElement>(null);
  const usersRef = useRef<HTMLDivElement>(null);
  const clientsRef = useRef<HTMLDivElement>(null);
  const hiddenRef = useRef<HTMLDivElement>(null); // Hidden element for initial focus reset

  const navigate = useNavigate();
  const { pageTab, siteUUID, option } = useParams();

  const { showAlert } = useAlert();
  const [opened, { open, close }] = useDisclosure(false);
  const dispatch = useAppDispatch();
  const { data } = useAppSelector((state) => state.user, shallowEqual);
  const [t] = useTranslation('account');
  const [c] = useTranslation('common');
  useDocumentTitle(t('title'));
  const [activeTab, setActiveTab] = useState<TabsValue>(pageTab || 'Account Details');
  // set role 
  const {updateNameCompany, getInfoIndustry, loadingUpdateCompanyName } = useAppSelector((state) => state.organization, shallowEqual);

  const navigateToWebsiteListHandler = (): void =>
    navigate({
      pathname: PATHS.DASHBOARD,
      search: '?focusTitle=true',
    });

  
  const validationFormSchema = yup.object().shape({
    firstName: validationPattern.firstName,
    lastName: validationPattern.lastName,
    companyName: validationPattern.companyName,
  });

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      companyName: data?.company_name
    },
    validate: (values) => {
      const verify = yupResolver(validationFormSchema)
      const result = verify(values)
      // console.log( result  );
      if (Object.keys(result).length > 0) {
        setNeedFocus(true)
      } else {
        setNeedFocus(false)
      }
      return result
    }
  });
  const { setNeedFocus } = useFormFocus({ formErrors: form.errors });

  const successMessage = (): void => {
    showAlert({
      id: 'GeneralInfo',
      title: c('Success'),
      message: `${t('generalInfoSuccess')}`,
      variant: 'success',
    });
  };

  const deleteCurrentSuccessMessage = (): void => {
    showAlert({
      id: 'CurrentUserDelete',
      title: c('Success'),
      message: `${t('currentUserDeleteSuccess')}`,
      variant: 'success',
    });
  };

  const onSubmitHandler = async (values: Record<string, string | undefined>): Promise<void> => {
    if (!values) {
      return;
    }
    let updatedData = { ...data };

    if (data?.company_name?.trim() !== values.companyName?.trim()) {
      const trimmedCompanyName = values.companyName?.trim();
      dispatch(updateCompanyName({ new_company_name: trimmedCompanyName, company_id: company_id  , successMessage}));
      updatedData.company_name = trimmedCompanyName;
    }

    dispatch(updateCurrent({
      data: {
        firstName: values?.firstName,
        lastName: values?.lastName
      },successMessage
    }));
    // Dispatch the update only if changes have been made
    if (updatedData !== data) {
      dispatch(updateCurrentUserOrganizationInfo({ data: updatedData }));
    }
  };

  useEffect(() => {
    if (data.firstName && data.lastName && !form.values.firstName && !form.values.lastName) {
      form.setValues({ firstName: data.firstName, lastName: data.lastName});
    }

  }, [data]);

  const changeAvailableAccount = (field: string, value: boolean): void => {
    dispatch(updateCurrent({ data: { [field]: value } }));
  };
  const deleteCurrentUser = async (): Promise<void> => {
    dispatch(deleteCurrent({ successMessage: deleteCurrentSuccessMessage }));
    await Auth.signOut();
    dispatch(setIsAuthorizeAction(false));
    removetOnboardingStatus();
    navigate(PATHS.LOGIN);
  };


  const handleTabChange = (value: TabsValue): void => {
    if (document.title != value) {
      document.title = String(value?.toString());
    }
    navigate(`/account/${value}`)

    // if(organizationId){
    //   navigate(`/account/Account Details`);
    // }
    setActiveTab(value);
    form.reset();
  };

  const handleFocus = (tab: string | TabsValue) => {
    if (tab === 'Account Details' && accountRef.current) {
      accountRef.current.focus();
    } else if (tab === 'Password' && passwordRef.current) {
      passwordRef.current.focus();
    } else if (tab === 'Notifications' && notificationsRef.current) {
      notificationsRef.current.focus();
    } else if (tab === 'Organisations' && organizationRef.current) {
      organizationRef.current.focus();
    } else if (tab === 'Users' && usersRef.current) {
      usersRef.current.focus();
    } else if (tab === 'Clients' && clientsRef.current) {
      clientsRef.current.focus();
    }
  };
  useEffect(() => {
    if (pageTab === ":pageTab") {
      navigate(`/account/Account Details`);
    } else {
      setActiveTab(pageTab || 'Account Details');
    }
    handleFocus(pageTab ?? 'Account Details');
  }, [pageTab, navigate]);

  useEffect(() => {
    // Set focus to the hidden element first
    if (hiddenRef.current) {
      hiddenRef.current.focus();
    }
    // After a short delay, set focus to the active tab content
    setTimeout(() => {
      window.addEventListener('keydown', handleKeyForHeadingLevelOne)
      handleFocus(activeTab);
      return () => {
        window.removeEventListener('keydown', handleKeyForHeadingLevelOne)
      }
    }, 100);
  }, [activeTab]);


  function handleKeyForHeadingLevelOne(event: any) {
    if (event.keyCode === 72 || event.keyCode === 39) {
      let ref: React.RefObject<HTMLDivElement> | null = null;

      switch (activeTab) {
        case 'Account Details':
          ref = accountRef;
          break;
        case 'Password':
          ref = passwordRef;
          break;
        case 'Notifications':
          ref = notificationsRef;
          break;
        case 'Organisations':
          ref = organizationRef;
          break;
        case 'Users':
          ref = usersRef;
          break;
        case 'Clients':
          ref = clientsRef;
          break;
        default:
          break;
      }

      if (ref && ref.current) {
        const h2Element = ref.current.querySelector('h1');
        if (h2Element) {
          h2Element.focus();
          // h2Element.style.border = '1px solid #5b5eff'
        }
      }
    }
  }

  const { company_id } = useAppSelector((state) => state.user.data, shallowEqual);
  // useEffect(() => {

  //   if (!updateNameCompany) {
  //     return;
  //   }

  //   console.log(updateNameCompany);

  //   showAlert({
  //     id: 'updateNameCompany',
  //     title: c('Success'),
  //     message: `company details updated sucessfully`,
  //     variant: 'success',
  //   });
  //   dispatch(updateCompanyNameMessage())

  // }, [updateNameCompany , updateCurrent])

  useEffect(() => {
    resetFocus()
    dispatch(getInfosIndustry());

  }, [])

  const titleRef = useRef<HTMLHeadingElement>(null)
  
  
  function resetFocus() {
      titleRef?.current?.setAttribute('tabindex', '-1');
      titleRef?.current?.focus();
  }


  return (
    <main className={styles.tabContainer}  >
      <div ref={hiddenRef} tabIndex={-1} style={{ position: 'absolute', left: '-9999px' }} />
      <div className={styles['right-content']}>
        <div className={styles['right-content']}>
          <Title ref={titleRef} className={styles['second-heading']} color={'#264547'} mb={'18px'} order={1}>
            {t('nav.accountDetails')}
          </Title>
          <form
            className={`${styles['general-info']}  roleForm`}
            onSubmit={form.onSubmit((values) => {
              onSubmitHandler(values);
              setNeedFocus(true);
            })}>
            <TextInput
              autoComplete={'given-name'}
              label={`${t('firstName')}`}
              withAsterisk={true}
              placeholder={`${t('firstNamePlaceholder')}`}
              {...form.getInputProps('firstName')}
            />
            <Space h={'md'} />
            <TextInput
              autoComplete={'family-name'}
              withAsterisk={true}
              label={`${t('lastName')}`}
              placeholder={`${t('lastNamePlaceholder')}`}
              {...form.getInputProps('lastName')}
            />
            <Space h={'md'} />
            <TextInput
              type='text'
              label={`Company Name`}
              placeholder={`Company Name`}
              {...form.getInputProps('companyName')}
            />
            <Space h={'md'} />
            <TextInput
              disabled
              type='email'
              label={`${t('nav.email')}`}
              placeholder={`Your Email`}
              {...form.getInputProps('email')}
              value={data.email}
            />
            <Space h={'md'} />
            <Flex gap={'md'}>
              <Button onClick={() => form.clearErrors()} className={styles['form-btn']} type={'submit'}>
                {t('updateInformation')}
              </Button>
              {/* <Button
                color='gray'
                ariaLabel='Undo changes made'
                className={styles['form-btn']}
                onClick={() => {
                  form.setValues({ firstName: data.firstName, lastName: data.lastName });
                  form.clearErrors();
                }}
              >
                {t('nav.undoAccount')}
              </Button> */}
              <Button buttonStyle={{ backgroundColor : "#F86868" , color : "#000"}} className={styles.deleteAccount} onClick={open}> Delete Account </Button>
            </Flex>

          </form>
          <Space h={'md'} />
          </div>
        <MyOrganisations focusTitle={false}  page="account-details" panel={option} siteUUID={siteUUID} />
      </div>
      <Modal opened={opened} close={close} zIndex={1000} withCloseButton={false} size={416} padding={0}>
        <Group align={'center'} position={'center'} p={28}>
          <Title order={2}>{t('removeSite')}</Title>
          <Text align={'center'}>
            {t('modalFirstRow')}
            <br />
            {t('modalSecondRow')}
          </Text>
        </Group>
        <div className={classNames(styles['btn-group'])}>
          <UnstyledButton onClick={close} className={classNames(styles['action-btn'])}>
            {t('modalCancel')}
          </UnstyledButton>
          <Divider orientation={'vertical'} color={'gray'} my={'none'}></Divider>
          <UnstyledButton onClick={deleteCurrentUser} className={classNames(styles['action-btn'])}>
            {t('modalDelete')}
          </UnstyledButton>
        </div>
      </Modal>

    </main>
  );
};

export default AccountPage;